import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { getAddons } from "../../../../shared/hooks/useFetchServiceDetails";
import API_URLS from "../../Utils/axr";
import store from "../store";
import DwLogutil from "../../Utils/DwLogutil";
import { errorConditionGTM } from "../../Utils/googleTags";
import { EC_DW_LOGGING_STEPS } from "../../../../shared/utils/Constants";

type MoversAddonProductStateType = {
  moversAddonProductData: any;
  addonAPILoading: boolean;
  error: any;
};

const initialState: MoversAddonProductStateType = {
  moversAddonProductData: null,
  addonAPILoading: false,
  error: null,
};

// Async thunk
export const getMoversAddonProducts = createAsyncThunk(
  "moversAddonProduct/fetch",
  async (payload: { addOnsProductsRequest: any }, { rejectWithValue }) => {
    try {
      const { addOnsProductsRequest } = payload;
      const reduxStore = store.getState();
      let headers = reduxStore?.brightspeedToken?.headers;
      headers = { ...headers, flow: "change" };

      const moversAddressQualificationData = reduxStore?.addressQualification?.moversData?.moversAddressQualification?.entity;
      const user = reduxStore?.user?.data;

      const siteId =
        moversAddressQualificationData?.siteId ||
        reduxStore?.addressQualification?.data?.addressQualification?.entity?.siteId ||
        headers?.siteId;

      const wireCenter =
        moversAddressQualificationData?.wireCenter ||
        reduxStore?.addressQualification?.data?.addressQualification?.entity?.wireCenter;

      const transactionId =
        moversAddressQualificationData?.partnerSessionId ||
        reduxStore?.addressQualification?.data?.addressQualification?.entity?.partnerSessionId ||
        "brspd-" + headers?.sessionId;

      const baseline = {
        transactionId: transactionId,
        channel: "web-acm",
        wirecenter: wireCenter,
        siteId: moversAddressQualificationData?.siteId,
        partnerOrderId: transactionId,
        intent: "",
        workflow: "mover",
        billingType: "postpaid",
      };

      const params = { "": [API_URLS?.addonsProducts] };
      const payloads = { baseline, addOnsProductsRequest };

      DwLogutil({ stepName: EC_DW_LOGGING_STEPS.movers_addon_product_api_triggered });

      const response = await getAddons(params, payloads, headers);

      DwLogutil({
        stepName:
          Number(response?.data?.messageStatus?.[0]?.statusCode) === 200
            ? EC_DW_LOGGING_STEPS.movers_addon_product_api_success
            : EC_DW_LOGGING_STEPS.movers_addon_product_api_failed,
        error:
          Number(response?.data?.messageStatus?.[0]?.statusCode) !== 200
            ? response?.data?.messageStatus?.[0]?.statusMessage
            : undefined,
      });

      return response?.data?.addOnsProductResponse || response?.data;
    } catch (error: any) {
      const errorMessage =
        error?.message || "Something went wrong while fetching Movers Addon Product";

      DwLogutil({
        stepName: EC_DW_LOGGING_STEPS.movers_addon_product_api_failed,
        error: errorMessage,
      });

      return rejectWithValue(errorMessage);
    }
  }
);

// Slice
const moversAddonProductSlice = createSlice({
  name: "moversAddonProduct",
  initialState,
  reducers: {
    clearMoversAddonProductSlice: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMoversAddonProducts.pending, (state) => {
        state.addonAPILoading = true;
        state.error = null;
      })
      .addCase(
        getMoversAddonProducts.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.addonAPILoading = false;
          state.moversAddonProductData = action.payload;
          state.error = null;
        }
      )
      .addCase(getMoversAddonProducts.rejected, (state, action) => {
        state.addonAPILoading = false;
        state.moversAddonProductData = null;
        state.error = action.payload;

        errorConditionGTM(
          "BRSPD_Fiber_EC_Flow",
          "movers addon product",
          action.error?.message,
          action?.type
        );
      });
  },
});

export const { clearMoversAddonProductSlice } = moversAddonProductSlice.actions;
export default moversAddonProductSlice.reducer;
